import LegendToggleIcon from "@mui/icons-material/LegendToggle";
import { Box, Button, IconButton, Tooltip as MuiTooltip, Typography, useMediaQuery, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { DateTime } from "luxon";
import { type FC, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Bar, CartesianGrid, ComposedChart, Legend, Line, Rectangle, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { CATEGORICAL_SERIES, INK, NEUTRAL } from "../../../../theme/tokens";
import { defaultChartResponsiveHeights, defaultChartResponsiveWidths } from "../common";
import { InHouseBarChartSkeleton } from "./InHouseBarChartSkeleton";
import type { InHouseBarChartProps, InHouseBarDataItem } from "./types";

type Row = {
	// X value: ms since epoch (startOf('day')) in time mode, or the category label in categorical mode.
	x: number | string;
	[seriesKey: string]: number | string | undefined;
};

type SeriesMeta = {
	key: string;
	name: string;
	color: string;
	stackId: string;
	minPointSize?: number;
	indexInStack: number;
	stackSize: number;
};

const DAY_MS = 12 * 60 * 60 * 1000;
const RIGHT_LINE_KEY = "__rightAxisLine__";

export const InHouseBarChart: FC<InHouseBarChartProps> = ({
	stackedGroups,
	leftYTickRenderer,
	rightYTickRenderer,
	rightAxisLineData,
	width = defaultChartResponsiveWidths,
	height = defaultChartResponsiveHeights,
	minDate,
	maxDate,
	margin = {
		top: 20,
		right: 20,
		bottom: 0,
		left: 0,
	},
	xAxisScale = "time",
	minTickGap = 26,
	hideLegend = false,
	xAxisType = "number",
	axisColor = NEUTRAL[600],
	xTickFormatter = (dateOrLabel) => (typeof dateOrLabel === "string" ? dateOrLabel : dateOrLabel.toFormat("MMM d")),
	xAxisHeight,
	barVisibleWidth = 10,
	barGap = 4,
	legendPadding = {
		inline: 24,
		block: 16,
	},
	legendLabelColor = alpha(INK.SECONDARY, 0.5),
	visibilityToggling = true,
	onSeriesVisibilityToggle,
	shrinkLegendForSmallScreens = false,
}) => {
	const { t } = useTranslation();
	const theme = useTheme();
	const isSmallScreen = useMediaQuery(theme.breakpoints.down(1530));
	const useShrunkLegend = shrinkLegendForSmallScreens && isSmallScreen && !hideLegend;
	const calculatedMargin = useMemo(
		() => ({
			...(useShrunkLegend ? { ...margin, bottom: margin.bottom + 12 } : margin),
		}),
		[margin, useShrunkLegend],
	);
	const [hiddenSeries, setHiddenSeries] = useState<Set<string>>(new Set());

	const toggleSeriesVisibility = useCallback(
		(seriesName: string) => {
			setHiddenSeries((prev) => {
				const next = new Set(prev);
				// Post-toggle visibility: a currently-hidden series is becoming visible. Notifying from
				// inside the updater keeps this callback's identity stable across toggles (the notifier
				// is analytics-only; production runs updaters once).
				const becomingVisible = next.has(seriesName);
				if (becomingVisible) {
					next.delete(seriesName);
				} else {
					next.add(seriesName);
				}
				onSeriesVisibilityToggle?.(seriesName, becomingVisible);
				return next;
			});
		},
		[onSeriesVisibilityToggle],
	);

	const isCategorical = xAxisType === "category";
	// The right-axis overlay line is date-keyed — time mode only.
	const rightLine = isCategorical ? undefined : rightAxisLineData;
	const series: SeriesMeta[] = [];
	// Time-mode X values (day buckets) and categorical-mode X values (labels in first-appearance order).
	const dateSet = new Set<number>();
	const categoryLabels: string[] = [];

	if (!stackedGroups) {
		return (
			<Box
				sx={{
					width,
					height,
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
				}}
			>
				<InHouseBarChartSkeleton showLegendSkeleton={!hideLegend && !useShrunkLegend} margin={calculatedMargin} />
			</Box>
		);
	}

	// The X key of a datapoint: the day bucket (ms) in time mode, or the label string in categorical mode.
	const xKeyOf = (item: InHouseBarDataItem): number | string =>
		isCategorical ? (item.dateOrLabel as string) : (item.dateOrLabel as DateTime).startOf("day").valueOf();

	const seenLabels = new Set<string>();
	stackedGroups.forEach((stack, sIdx) => {
		const stackId = `stack-${sIdx}`;
		stack.forEach((g, gIdx: number) => {
			series.push({
				key: `${stackId}:${g.name}`,
				name: g.name,
				color: g.color ?? CATEGORICAL_SERIES[(sIdx + gIdx) % CATEGORICAL_SERIES.length],
				stackId,
				minPointSize: g.minSize,
				indexInStack: gIdx,
				stackSize: stack.length,
			});
			g.data.forEach((d) => {
				if (isCategorical) {
					const label = d.dateOrLabel as string;
					if (!seenLabels.has(label)) {
						seenLabels.add(label);
						categoryLabels.push(label);
					}
				} else {
					dateSet.add((d.dateOrLabel as DateTime).startOf("day").valueOf());
				}
			});
		});
	});

	if (rightLine) {
		rightLine.data.forEach((d) => {
			dateSet.add(d.date.startOf("day").valueOf());
		});
	}

	if (series.length === 0 || (isCategorical ? categoryLabels.length === 0 : dateSet.size === 0)) {
		return (
			<Box
				sx={{
					width,
					height,
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
				}}
			>
				<Typography
					sx={{
						color: "text.secondary",
					}}
				>
					{t("No Data")}
				</Typography>
			</Box>
		);
	}

	const allDates = Array.from(dateSet).sort((a, b) => a - b);

	// Build aligned rows keyed by the X value: time mode keys by day, categorical mode by label (in given order).
	const xValues: (number | string)[] = isCategorical ? categoryLabels : allDates;
	const chartData: Row[] = xValues.map((x) => {
		const r: Row = { x };
		series.forEach((s) => {
			r[s.key] = undefined;
		});
		if (rightLine) {
			r[RIGHT_LINE_KEY] = undefined;
		}
		return r;
	});
	const byX = new Map<number | string, Row>(chartData.map((r) => [r.x, r]));

	// Inject bar values
	stackedGroups.forEach((stack, sIdx) => {
		const stackId = `stack-${sIdx}`;
		stack.forEach((g) => {
			const key = `${stackId}:${g.name}`;
			g.data.forEach((d) => {
				const row = byX.get(xKeyOf(d));
				if (row) row[key] = d.value;
			});
		});
	});

	// Inject line values (right axis) — time mode only
	if (rightLine) {
		rightLine.data.forEach((d) => {
			const row = byX.get(d.date.startOf("day").valueOf());
			if (row) row[RIGHT_LINE_KEY] = d.value;
		});
	}

	// Tooltip formatting – simple, works for bars & line
	const tooltipFormatter = (value: any, name: string | number | undefined) => [value, name];
	const labelFormatter = (label: any) => (isCategorical ? String(label) : DateTime.fromMillis(Number(label)).toFormat("EEEE, MMMM d"));

	// Time-mode X domain: pad the data by one day each side unless the caller pins min/max.
	// undefined in categorical mode, where `allDates` is empty and the math would be NaN.
	const timeDomain: [number, number] | undefined = isCategorical
		? undefined
		: (() => {
				const dataMin = allDates[0];
				const dataMax = allDates[allDates.length - 1];
				const padMin = minDate ? minDate.startOf("day").valueOf() : dataMin - DAY_MS; // one day before earliest
				const padMax = maxDate ? maxDate.startOf("day").valueOf() : dataMax + DAY_MS; // one day after latest
				return [Math.min(padMin, dataMin - DAY_MS), Math.max(padMax, dataMax + DAY_MS)];
			})();

	/** Group series keys by stack for use in shape renderer */
	const keysByStack = new Map<string, string[]>();
	for (const s of series) {
		if (!keysByStack.has(s.stackId)) keysByStack.set(s.stackId, []);
		keysByStack.get(s.stackId)?.push(s.key);
	}

	/** Map series keys to their display names for hidden-series lookup */
	const keyToName = new Map<string, string>(series.map((s) => [s.key, s.name]));

	/** Factory to create a shape renderer that rounds only if this bar is the top visible bar on this day */
	const makeRoundedShape = (stackId: string, dataKey: string) => (props: any) => {
		const { payload, ...rest } = props;
		const stackKeys = keysByStack.get(stackId) ?? [];
		const visibleKeys = stackKeys.filter((k) => !hiddenSeries.has(keyToName.get(k) ?? ""));
		const topKey = [...visibleKeys].reverse().find((k) => ((payload[k] ?? 0) as number) > 0);
		const isTop = topKey === dataKey;
		const radius = isTop ? [6, 6, 0, 0] : [0, 0, 0, 0];
		return <Rectangle {...rest} payload={payload} radius={radius} />;
	};

	return (
		<Box
			sx={{
				width,
				height,
				display: "flex",
				justifyContent: "center",
				alignItems: "center",
			}}
		>
			<ResponsiveContainer width="100%" height="100%">
				<ComposedChart
					key={`${isCategorical ? categoryLabels.length : allDates.length}-${series.map((s) => s.key).join()}`}
					data={chartData}
					margin={calculatedMargin}
					barSize={barVisibleWidth}
					barGap={barGap}
				>
					<CartesianGrid strokeDasharray="2 2" yAxisId={"left"} />
					<XAxis
						dataKey="x"
						type={xAxisType}
						{...(timeDomain ? { domain: timeDomain, scale: xAxisScale } : { textAnchor: "end" })}
						tickFormatter={isCategorical ? (label) => xTickFormatter(String(label)) : (ms) => xTickFormatter(DateTime.fromMillis(Number(ms)))}
						tickMargin={hideLegend ? 16 : 18}
						fontWeight={700}
						fontSize={10}
						tickSize={0}
						stroke={axisColor}
						minTickGap={minTickGap}
						angle={-45}
						height={xAxisHeight}
					/>
					{/* Left axis – bars */}
					<YAxis yAxisId="left" stroke={axisColor} tickFormatter={leftYTickRenderer} />
					{/* Right axis – line (only if provided) */}
					{rightLine && <YAxis yAxisId="right" stroke={axisColor} orientation="right" tickFormatter={rightYTickRenderer} />}

					<Tooltip formatter={tooltipFormatter} labelFormatter={labelFormatter} cursor={{ opacity: 0.45 }} />
					{!hideLegend && !useShrunkLegend && (
						<Legend
							iconType={"circle"}
							align={"left"}
							verticalAlign={"bottom"}
							wrapperStyle={{
								paddingBlock: legendPadding.block,
								paddingInline: legendPadding.inline,
							}}
							content={(props) => {
								const { payload } = props;
								// Recharts hands the legend payload in its own (alphabetical-ish) order; in categorical
								// mode the segment order is semantic (the caller's stack order), so restore it. Time
								// mode keeps the payload untouched (previous behavior).
								const seriesOrder = new Map(series.map((s, index) => [s.name, index]));
								const orderedPayload =
									isCategorical && payload
										? [...payload].sort(
												(a, b) => (seriesOrder.get(String(a.value)) ?? Number.MAX_SAFE_INTEGER) - (seriesOrder.get(String(b.value)) ?? Number.MAX_SAFE_INTEGER),
											)
										: payload;
								return (
									<div style={{ display: "flex", gap: "16px" }}>
										{orderedPayload?.map((entry) => {
											const isHidden = hiddenSeries.has(entry.value as string);
											const itemSx = {
												display: "flex",
												alignItems: "center",
												border: "none",
												gap: "4px",
												cursor: visibilityToggling ? "pointer" : "default",
												opacity: isHidden ? 0.35 : 1,
												height: "fit-content",
												width: "fit-content",
												p: 0,
											} as const;
											const itemContent = (
												<>
													<div
														aria-hidden="true"
														style={{
															width: "12px",
															height: "12px",
															backgroundColor: entry.color,
															borderRadius: "50%",
														}}
													/>
													<span
														style={{
															color: legendLabelColor,
															fontSize: 12,
															fontWeight: 700,
															textDecoration: isHidden ? "line-through" : "none",
														}}
													>
														{entry.value}
													</span>
												</>
											);
											if (!visibilityToggling) {
												return (
													<Box component="span" key={`legend-item-${entry.value}`} sx={itemSx}>
														{itemContent}
													</Box>
												);
											}
											return (
												<Button
													type="button"
													key={`legend-item-${entry.value}`}
													sx={itemSx}
													onClick={(e) => {
														e.preventDefault();
														e.stopPropagation();
														toggleSeriesVisibility(entry.value as string);
													}}
													onKeyDown={(e) => {
														if (e.key === "Enter" || e.key === " ") {
															e.preventDefault();
															e.stopPropagation();
															toggleSeriesVisibility(entry.value as string);
														}
													}}
												>
													{itemContent}
												</Button>
											);
										})}
									</div>
								);
							}}
						/>
					)}

					{/* Bars (left axis) */}
					{series.map((s) => (
						<Bar
							key={s.key}
							dataKey={s.key}
							name={s.name}
							stackId={s.stackId}
							fill={s.color}
							yAxisId="left"
							{...(s.minPointSize !== undefined ? { minPointSize: s.minPointSize } : {})}
							shape={makeRoundedShape(s.stackId, s.key)}
							hide={hiddenSeries.has(s.name)}
						/>
					))}

					{/* Right-axis line */}
					{rightLine && (
						<Line
							type={rightLine.graphCurve ?? "monotone"}
							dataKey={RIGHT_LINE_KEY}
							yAxisId="right"
							name={rightLine.name}
							stroke={rightLine.color ?? axisColor}
							strokeWidth={2}
							dot={false}
							connectNulls
							isAnimationActive={false}
							hide={hiddenSeries.has(rightLine.name)}
						/>
					)}
				</ComposedChart>
			</ResponsiveContainer>
			{useShrunkLegend && (
				<MuiTooltip
					placement="top"
					title={
						<Box sx={{ display: "flex", flexDirection: "column", gap: "6px", p: "4px" }}>
							{series.map((s) => (
								<Box key={s.key} sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
									<Box sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: s.color, flexShrink: 0 }} />
									<Typography sx={{ fontSize: 12, fontWeight: 600, color: INK.ON_DARK, whiteSpace: "nowrap" }}>{s.name}</Typography>
								</Box>
							))}
							{rightLine && (
								<Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
									<Box sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: rightLine.color ?? axisColor, flexShrink: 0 }} />
									<Typography sx={{ fontSize: 12, fontWeight: 600, color: INK.ON_DARK, whiteSpace: "nowrap" }}>{rightLine.name}</Typography>
								</Box>
							)}
						</Box>
					}
				>
					<IconButton disableRipple={true} size="small" sx={{ alignSelf: "flex-start" }} aria-label={t("Show legend")}>
						<LegendToggleIcon sx={{ fontSize: 18, color: axisColor }} />
					</IconButton>
				</MuiTooltip>
			)}
		</Box>
	);
};

export default InHouseBarChart;
